var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { initialize as LDInitialize, basicLogger as LDBasicLogger, } from 'launchdarkly-js-client-sdk';
import { system } from '@config/system';
import { getLaunchDarklyApiKey } from '@config/index';
import { PipelineFeatureFlags } from './FeatureFlag';
const MAX_INITIALIZATION_TIME_MS = 4000;
/**
 * Rejects if `promise` has not settled in `timeoutMs`. `identify()` carries no
 * deadline of its own.
 */
const withTimeout = (promise, timeoutMs) => {
    let timer;
    return Promise.race([
        Promise.resolve(promise),
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('identify timeout')), timeoutMs);
        }),
        // The loser's timer stays armed once the race settles.
    ]).finally(() => clearTimeout(timer));
};
class FeatureFlagManager {
    constructor() {
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "events", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "hasFlagData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isPropertyScopedContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "overrideData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
    }
    clearOverrides() {
        this.overrideData = {};
        this.dispatchEvent('change');
    }
    createClient(context, isPropertyContext) {
        return new Promise((res) => {
            let hasSettled = false;
            const settle = () => {
                if (hasSettled)
                    return;
                hasSettled = true;
                clearTimeout(initializationTimeout);
                this.dispatchEvent('change');
                res();
            };
            const initializationTimeout = setTimeout(() => {
                console.warn('Feature Flags initialization timeout, using defaults');
                settle();
            }, MAX_INITIALIZATION_TIME_MS);
            // Initialize with the default set of flags
            this.data = Object.assign({}, PipelineFeatureFlags);
            this.isPropertyScopedContext = isPropertyContext;
            this.hasFlagData = false;
            this.client = LDInitialize(getLaunchDarklyApiKey(), this.getLaunchDarklyContext(context), {
                // bootstrap is the initial value for flags, default is 'localStorage'
                // which cause issues when user rejected CMP consents
                bootstrap: this.data,
                diagnosticOptOut: true,
                logger: LDBasicLogger({ level: 'none' }),
                sendEvents: false,
                sendLDHeaders: false,
            });
            // Fires on every flag update for the life of the client, so it must not
            // set which context the values belong to — `createClient` and
            // `identifyContext` own that.
            this.client.on('change', () => {
                this.onReceiveFlagData();
                this.hasFlagData = true;
                settle();
            });
        });
    }
    dispatchEvent(event) {
        // Copy first: a listener may unsubscribe while we iterate.
        [...(this.events[event] || [])].forEach((c) => c === null || c === void 0 ? void 0 : c());
    }
    getLaunchDarklyContext(context) {
        const id = context === 'anonymous' ? 0 : context.id;
        return {
            key: `property:${id}`,
            kind: 'user',
            name: context === 'anonymous' ? 'Anonymous Context' : context.name,
        };
    }
    /**
     * Whether the flag values on hand were evaluated against a property, rather
     * than the anonymous context every session starts on. Anything a property can
     * target must wait for this. Returns to false when an evaluation fails, since
     * the SDK goes on serving the previous context's values.
     */
    hasPropertyContext() {
        return this.isPropertyScopedContext && this.hasFlagData;
    }
    identifyContext(context, isPropertyContext) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const flags = yield withTimeout((_a = this.client) === null || _a === void 0 ? void 0 : _a.identify(this.getLaunchDarklyContext(context)), MAX_INITIALIZATION_TIME_MS);
                this.data = flags || ((_b = this.client) === null || _b === void 0 ? void 0 : _b.allFlags()) || {};
                this.isPropertyScopedContext = isPropertyContext;
                this.hasFlagData = true;
            }
            catch (_c) {
                // The SDK stays on the previous context's values, so what we hold is not
                // this property's answer.
                console.warn('Feature Flags identify failed, keeping previous values');
                this.isPropertyScopedContext = false;
            }
            finally {
                this.dispatchEvent('change');
            }
        });
    }
    /**
     * Points the client at a context and waits for that context's flag values.
     * The first call builds the client; later ones switch context through
     * `identify()`, which reloads every flag value and resolves with the new set.
     * Every path dispatches `change`, the timeout included, so consumers re-read
     * once the attempt has settled.
     */
    initialize(_a) {
        return __awaiter(this, arguments, void 0, function* ({ context }) {
            const isPropertyContext = context !== 'anonymous';
            if (system.isPlaywright || system.isIntegrationTest) {
                this.data = Object.assign({}, PipelineFeatureFlags);
                this.isPropertyScopedContext = isPropertyContext;
                this.hasFlagData = true;
                // The harness serves its own values, but consumers still need to hear
                // that the context moved.
                this.dispatchEvent('change');
                return;
            }
            if (this.client) {
                yield this.identifyContext(context, isPropertyContext);
                return;
            }
            yield this.createClient(context, isPropertyContext);
        });
    }
    isEnabled(flag) {
        return this.overrideData[flag] !== undefined
            ? this.overrideData[flag]
            : this.data[flag];
    }
    off(event, callback) {
        if (this.events[event] === undefined)
            return;
        const index = this.events[event].indexOf(callback);
        // An unknown callback would otherwise splice(-1) and drop somebody else's
        // subscription — cheap to ignore, expensive to debug.
        if (index === -1)
            return;
        this.events[event].splice(index, 1);
    }
    on(event, callback) {
        if (this.events[event] === undefined) {
            this.events[event] = [];
        }
        this.events[event].push(callback);
    }
    onReceiveFlagData() {
        var _a;
        this.data = ((_a = this.client) === null || _a === void 0 ? void 0 : _a.allFlags()) || {};
        // Tell subscribers the values moved. Without this, `useFeatureFlag` keeps
        // whatever it read when it mounted: consumers that mount before LaunchDarkly
        // answers — or before `initialize` runs again with the property context —
        // would stay on the bootstrap value forever.
        this.dispatchEvent('change');
    }
    override(data) {
        this.overrideData = Object.assign(Object.assign({}, this.overrideData), data);
        this.dispatchEvent('change');
    }
}
const instance = new FeatureFlagManager();
// Apply local feature flag overrides from .local-feature-flags.json (dev/test only)
// The file is injected at build time via webpack DefinePlugin in config-overrides.js
if (['development', 'test'].includes(system.nodeEnv || '') &&
    typeof __LOCAL_FEATURE_FLAGS__ !== 'undefined' &&
    Object.keys(__LOCAL_FEATURE_FLAGS__).length > 0) {
    instance.override(__LOCAL_FEATURE_FLAGS__);
    console.info('[FeatureFlags] Local overrides applied from .local-feature-flags.json:', __LOCAL_FEATURE_FLAGS__);
}
// @TECHDEBT: In order to test, we can use this global function
// that is a temporary solution and should be replaced
// by a different solution later (like a chrome extension for instance)
// Ideally all the test functions should be centralized in a unique parent global object
window['overrideFeatureFlag'] = (key, value) => {
    instance.override({ [key]: value });
};
window['overrideFeatureFlags'] = (data) => {
    instance.override(data);
};
export default instance;
